// assets/custom-subcategory-nav.js

(function () {
  'use strict';

  const SELECTORS = {
    root:  '[data-component="subcategory-nav"]',
    track: '[data-subnav-track]',
    prev:  '[data-subnav-prev]',
    next:  '[data-subnav-next]',
    dots:  '[data-subnav-dots]',
    link:  '.subnav__link',
    item:  '.subnav__item',
  };

  const instances = new Map();

  class SubcategoryNav {
    constructor(el) {
      this.el            = el;
      this.track         = el.querySelector(SELECTORS.track);
      this.dotsEl        = el.querySelector(SELECTORS.dots);
      this.scrollable    = el.dataset.scrollable === 'true';
      this.source        = el.dataset.source || 'from-megamenu';
      this.currentHandle = el.dataset.currentCollection || '';
      this.navStyle      = el.dataset.navStyle || 'cards';
      this._init();
    }

    _init() {
      if (this.source === 'from-megamenu') {
        this._renderFromMegamenu();
      }
      this._detectActive();
      this._bindArrows();
      this._buildDots();
      this._bindSlide();
      this._bindDrag();
      this._scrollActiveIntoView();
      this._updateControls();
    }

    // Reads all mfb-col-config-tile <script> elements injected by megamenu-panel.liquid
    // and renders them as collection tile links in the nav track.
    _renderFromMegamenu() {
      if (!this.track) return;

      // Remove any previously rendered megamenu tiles to prevent duplication
      // when shopify:section:load fires after a Theme Editor setting change.
      this.track.querySelectorAll('[data-megamenu-tile]').forEach(li => li.remove());

      const tiles = Array.from(document.querySelectorAll('.mfb-col-config-tile'));
      if (!tiles.length) return;

      // Dedupe by collection handle — the same collection can be referenced by
      // more than one mega panel block (e.g. a sub that points at a main collection).
      const seen = new Set();

      tiles.forEach(script => {
        let data;
        try { data = JSON.parse(script.textContent); } catch { return; }
        if (!data || !data.handle || !data.url) return;
        if (seen.has(data.handle)) return;
        seen.add(data.handle);

        const isActive = data.handle === this.currentHandle;
        const li = document.createElement('li');
        li.className = 'subnav__item';
        li.dataset.megamenuTile = '';

        const a = document.createElement('a');
        a.href = data.url;
        a.className = 'subnav__link' + (isActive ? ' is-active' : '');
        if (isActive) a.setAttribute('aria-current', 'true');
        a.dataset.collectionHandle = data.handle;

        if (data.image) {
          const img = document.createElement('img');
          img.src       = data.image;
          img.alt       = data.title || '';
          img.className = 'subnav__icon';
          img.loading   = 'lazy';
          a.appendChild(img);
        }

        const displayLabel = data.navLabel
          ? (data.title || '') + ' (' + data.navLabel + ')'
          : (data.title || '');

        if (this.navStyle === 'cards') {
          const label = document.createElement('span');
          label.className = 'subnav__card-label';
          label.textContent = displayLabel;
          a.appendChild(label);
        } else {
          const span = document.createElement('span');
          span.textContent = displayLabel;
          a.appendChild(span);
        }

        li.appendChild(a);
        this.track.appendChild(li);
      });
    }

    // Client-side active state: checks collection handle match for tile links.
    _detectActive() {
      this.el.querySelectorAll(SELECTORS.link).forEach(link => {
        const linkColl = link.dataset.collectionHandle ?? '';
        if (linkColl && linkColl === this.currentHandle && !link.classList.contains('is-active')) {
          link.classList.add('is-active');
          link.setAttribute('aria-current', 'true');
        }
      });
    }

    _scrollActiveIntoView() {
      if (!this.scrollable || !this.track) return;
      const active = this.track.querySelector(`${SELECTORS.link}.is-active`);
      if (!active) return;
      requestAnimationFrame(() => {
        const trackRect  = this.track.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();
        const offset = activeRect.left - trackRect.left - (trackRect.width - activeRect.width) / 2;
        this.track.scrollBy({ left: offset, behavior: 'smooth' });
      });
    }

    _bindArrows() {
      this.prevBtn = this.el.querySelector(SELECTORS.prev);
      this.nextBtn = this.el.querySelector(SELECTORS.next);
      if (!this.track || (!this.prevBtn && !this.nextBtn)) return;

      this.prevBtn?.addEventListener('click', () => this._scrollByPage(-1));
      this.nextBtn?.addEventListener('click', () => this._scrollByPage(1));
    }

    // Advance by roughly one visible page of tiles for a true "slide" feel,
    // leaving one tile of overlap so nothing is skipped.
    _scrollByPage(dir) {
      if (!this.track) return;
      const gap   = parseInt(getComputedStyle(this.track).gap) || 12;
      const first = this.track.querySelector(SELECTORS.link);
      const tile  = first ? first.offsetWidth + gap : 120;
      const page  = Math.max(tile, this.track.clientWidth - tile);
      this.track.scrollBy({ left: dir * page, behavior: 'smooth' });
    }

    // Mouse click-drag to scroll (desktop). Browsers only scroll overflow
    // containers via touch/trackpad/wheel — a mouse drag does nothing natively,
    // so we wire it up manually. Touch is left to the native scroller.
    _bindDrag() {
      if (!this.track) return;

      let isDown = false;
      let startX = 0;
      let startScroll = 0;
      let moved = false;
      const DRAG_THRESHOLD = 4; // px before a press counts as a drag

      // NOTE: no setPointerCapture — capturing the pointer on the track retargets the
      // trailing click to the track, so clicking a tile would not navigate to its
      // collection. Track the drag with window listeners so clicks reach the tile link.
      const onMove = (e) => {
        if (!isDown) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > DRAG_THRESHOLD) moved = true;
        if (moved) {
          e.preventDefault();
          this.track.scrollLeft = startScroll - dx;
        }
      };

      const onUp = () => {
        if (!isDown) return;
        isDown = false;
        this.track.classList.remove('is-dragging');
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };

      const onDown = (e) => {
        if (e.pointerType && e.pointerType !== 'mouse') return; // touch = native scroll
        if (e.button !== 0) return;                              // left button only
        isDown = true;
        moved = false;
        startX = e.clientX;
        startScroll = this.track.scrollLeft;
        this.track.classList.add('is-dragging');
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      };

      // Swallow the click that fires after a drag so it doesn't navigate the tile.
      const onClick = (e) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        }
      };

      // Stop the browser's native image/link ghost-drag from hijacking the gesture.
      const onDragStart = (e) => e.preventDefault();

      this.track.addEventListener('pointerdown', onDown);
      this.track.addEventListener('click', onClick, true);
      this.track.addEventListener('dragstart', onDragStart);

      this._drag = { onDown, onMove, onUp, onClick, onDragStart };
    }

    // Returns the tile <li> elements currently in the track.
    _tiles() {
      return this.track ? Array.from(this.track.querySelectorAll(SELECTORS.item)) : [];
    }

    // Scroll left needed to align a tile to the start of the viewport.
    _tileScrollLeft(tile) {
      return tile.offsetLeft - this.track.offsetLeft;
    }

    // Build one dot per tile. Dots are populated here (not in Liquid) because
    // from-megamenu tiles are rendered client-side.
    _buildDots() {
      if (!this.dotsEl) return;
      this.dotsEl.innerHTML = '';
      const tiles = this._tiles();
      this.dots = tiles.map((tile, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'subnav__dot';
        dot.setAttribute('role', 'tab');
        const label = tile.querySelector('.subnav__card-label, .subnav__link');
        dot.setAttribute('aria-label', (label ? label.textContent.trim() : '') || `Slide ${i + 1}`);
        dot.addEventListener('click', () => {
          this.track.scrollTo({ left: this._tileScrollLeft(tile), behavior: 'smooth' });
        });
        this.dotsEl.appendChild(dot);
        return dot;
      });
    }

    // Keep arrow + dot visibility/state in sync with overflow and scroll position.
    _bindSlide() {
      if (!this.track) return;

      let ticking = false;
      this._onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          this._updateControls();
          ticking = false;
        });
      };
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(() => this._updateControls());
        this._ro.observe(this.track);
      } else {
        window.addEventListener('resize', this._onScroll);
      }
    }

    _updateControls() {
      if (!this.track) return;

      const overflowing = this.track.scrollWidth > this.track.clientWidth + 1;
      const scrollLeft  = this.track.scrollLeft;
      const atStart = scrollLeft <= 1;
      const atEnd   = scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;

      // Grab cursor only when there is something to drag.
      this.track.classList.toggle('is-grabbable', overflowing);

      // Arrows
      [this.prevBtn, this.nextBtn].forEach(btn => {
        if (btn) btn.classList.toggle('is-hidden', !overflowing);
      });
      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;

      // Dots
      if (this.dotsEl) {
        this.dotsEl.classList.toggle('is-hidden', !overflowing);
        if (overflowing && this.dots && this.dots.length) {
          const tiles = this._tiles();
          let activeIdx = 0;
          if (atEnd) {
            activeIdx = tiles.length - 1;
          } else {
            // The tile whose start is closest to the current scroll position.
            let best = Infinity;
            tiles.forEach((tile, i) => {
              const dist = Math.abs(this._tileScrollLeft(tile) - scrollLeft);
              if (dist < best) { best = dist; activeIdx = i; }
            });
          }
          this.dots.forEach((dot, i) => {
            const active = i === activeIdx;
            dot.classList.toggle('is-active', active);
            dot.setAttribute('aria-selected', active ? 'true' : 'false');
          });
        }
      }
    }

    destroy() {
      if (this.track && this._onScroll) {
        this.track.removeEventListener('scroll', this._onScroll);
      }
      if (this.track && this._drag) {
        const d = this._drag;
        this.track.removeEventListener('pointerdown', d.onDown);
        this.track.removeEventListener('click', d.onClick, true);
        this.track.removeEventListener('dragstart', d.onDragStart);
        window.removeEventListener('pointermove', d.onMove);
        window.removeEventListener('pointerup', d.onUp);
      }
      this._ro?.disconnect();
      window.removeEventListener('resize', this._onScroll);
    }
  }

  function init(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach(el => {
      if (instances.has(el)) return;
      instances.set(el, new SubcategoryNav(el));
    });
  }

  function destroy(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach(el => {
      if (instances.has(el)) {
        instances.get(el).destroy();
        instances.delete(el);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load',   e => init(e.target));
  document.addEventListener('shopify:section:unload', e => destroy(e.target));

})();
